import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { CampoData } from '../../componentes/formulario';
import { anunciarOcultos } from '../../componentes/sensivel';
import { Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { get } from '../../nucleo/api';
import { data, dinheiro } from '../../nucleo/formato';
import { descricaoGerada } from '../../nucleo/texto-sistema';
import { corDoValor, hojeLocal, type Conta, type ExtratoConta as Extrato } from './tipos';
import { rotuloCategoria } from '../../nucleo/rotulos';
import { rotuloNomeForma } from '../../nucleo/forma-pagamento';

/**
 * Fase 2, W7 (B8): extrato da conta com saldo corrido. Saldo anterior, cada lançamento (transferência com o outro lado e
 * estorno como linha própria) com o saldo depois dele e o saldo final, que o servidor confere contra o saldo da conta.
 */
export function ExtratoConta({ conta, aoFechar }: { conta: Conta | null; aoFechar: () => void }) {
  if (!conta) return null;
  return <Corpo conta={conta} aoFechar={aoFechar} />;
}

function Corpo({ conta, aoFechar }: { conta: Conta; aoFechar: () => void }) {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const hoje = hojeLocal();
  const [de, setDe] = useState(`${hoje.slice(0, 7)}-01`);
  const [ate, setAte] = useState(hoje);
  const q = useQuery({
    queryKey: ['/financeiro/contas', 'extrato', conta.id, de, ate],
    queryFn: () => get<Extrato>(`/financeiro/contas/${conta.id}/extrato`, { de: de || undefined, ate: ate || undefined }),
  });
  const moeda = conta.moeda;
  return (
    <Modal
      aberto
      largura="lg"
      aoFechar={aoFechar}
      titulo={t('extrato.titulo', { conta: conta.nome })}
      rodape={
        <Botao variante="fantasma" onClick={aoFechar}>
          {tn('comum.fechar')}
        </Botao>
      }
    >
      <div className="mb-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        <div className="sm:w-44">
          <CampoData type="date" rotulo={t('filtros.de')} value={de} onChange={(e) => setDe(e.target.value)} />
        </div>
        <div className="sm:w-44">
          <CampoData type="date" rotulo={t('filtros.ate')} value={ate} onChange={(e) => setAte(e.target.value)} />
        </div>
      </div>
      <Estado consulta={q}>
        {(d) => (
          <div className="flex min-w-0 flex-col gap-3">
            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {(
                [
                  ['saldoAnterior', d.saldo_anterior_centavos, ''],
                  ['entradas', d.entradas_centavos, 'text-verde'],
                  ['saidas', d.saidas_centavos, 'text-perigo'],
                  ['saldoFinal', d.saldo_final_centavos, d.saldo_final_centavos < 0 ? 'text-perigo' : ''],
                ] as const
              ).map(([k, v, cor]) => (
                <div key={k} className="min-w-0 rounded-lg border border-borda bg-sup-2 px-3 py-2">
                  <dt className="kicker">{t(`extrato.${k}`)}</dt>
                  <TextoCortado como="dd" className={`numero text-base font-semibold ${cor}`}>{dinheiro(v, moeda)}</TextoCortado>
                </div>
              ))}
            </dl>
            {d.linhas.length === 0 ? (
              <p className="text-sm text-tinta-3">{t('extrato.vazio')}</p>
            ) : (
              <div tabIndex={0} role="region" aria-label={t('extrato.tabela')} className="max-h-96 overflow-auto rounded-lg border border-borda focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-sup-1">
                    <tr className="border-b border-borda text-left text-tinta-3">
                      <th scope="col" className="px-3 py-2 font-medium">{t('extrato.data')}</th>
                      <th scope="col" className="px-3 py-2 font-medium">{t('extrato.descricao')}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('extrato.valor')}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('extrato.saldo')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.linhas.map((l) => (
                      <tr key={l.id} className="border-b border-borda last:border-0">
                        <td className="numero px-3 py-2 whitespace-nowrap text-tinta-2">{data(l.data_caixa)}</td>
                        <td className="max-w-0 px-3 py-2">
                          <TextoCortado className="block">{descricaoGerada(l.descricao)}</TextoCortado>
                          <TextoCortado className="block text-xs text-tinta-3">
                            {[
                              l.tipo === 'transferencia' && l.outra_conta_nome ? t(l.efeito_centavos < 0 ? 'extrato.transferenciaPara' : 'extrato.transferenciaDe', { conta: l.outra_conta_nome }) : t(`tipo.${l.tipo}`),
                              rotuloCategoria(l.categoria_nome),
                              l.forma_pagamento_nome ? rotuloNomeForma(l.forma_pagamento_nome) : null,
                              l.estorno_de ? t('estado.estorno') : l.estornado_por ? t('estado.estornado') : null,
                              l.conciliado ? t('estado.conciliado') : null,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </TextoCortado>
                        </td>
                        <td className={`numero px-3 py-2 text-right whitespace-nowrap ${corDoValor(l.efeito_centavos < 0 ? 'saida' : 'entrada')}`}>{anunciarOcultos(dinheiro(l.efeito_centavos, moeda))}</td>
                        <td className={`numero px-3 py-2 text-right font-medium whitespace-nowrap ${l.saldo_centavos < 0 ? 'text-perigo' : ''}`}>{anunciarOcultos(dinheiro(l.saldo_centavos, moeda))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {d.truncado && <p className="text-xs text-tinta-3">{t('extrato.truncado', { n: d.linhas.length, total: d.total_linhas })}</p>}
            <p className="text-xs text-tinta-3">{t('extrato.fecha')}</p>
          </div>
        )}
      </Estado>
    </Modal>
  );
}
